import type { FlightLeg, ImportPlan, ImportScope } from './types'

/**
 * Period-scoped upsert — the rule that makes a re-import safe.
 *
 * BUSINESS-RULES 4.2 forbids the original "anything not in the file is
 * removed", because a file covering one week would then wipe the rest of the
 * month. What replaces it is the same idea bounded by a declared period:
 *
 *   A file for 01–30 Sep loads the month.
 *   A file for 15–28 Sep then replaces those fourteen days OUTRIGHT — every
 *   leg in them comes from the new file, and a leg that used to be there and
 *   is not in it any more is withdrawn.
 *   01–14 and 29–30 Sep are not read, not compared, not touched.
 *
 * "Outright" is the part that has to be exact. A flight missing from the new
 * file inside the period is not an omission to be preserved; it is a flight
 * that no longer operates. It is withdrawn rather than deleted, because a
 * cancellation is information the shift needs (BUSINESS-RULES 4.2: never hard
 * delete), and because somebody will ask tomorrow where VN1234 went.
 *
 * Files in one import are applied in order, so two overlapping periods settle
 * the same way twice, and the preview counts the end state rather than the
 * sum of the steps.
 */

export interface ScopedFile {
  scope: ImportScope
  legs: FlightLeg[]
}

export interface ImportResult {
  legs: FlightLeg[]
  plan: ImportPlan
}

function inScope(date: string, scope: ImportScope): boolean {
  return date >= scope.from && date <= scope.to
}

/** Fields whose change makes a leg "changed" rather than "unchanged". */
function sameLeg(a: FlightLeg, b: FlightLeg): boolean {
  return (
    a.regn === b.regn &&
    a.acType === b.acType &&
    a.serviceType === b.serviceType &&
    a.stdUtc === b.stdUtc &&
    a.staUtc === b.staUtc &&
    a.etdUtc === b.etdUtc &&
    a.etaUtc === b.etaUtc &&
    a.blockMinutes === b.blockMinutes &&
    a.cancelledAt === b.cancelledAt
  )
}

export function planImport(existing: FlightLeg[], files: ScopedFile[], at: string): ImportResult {
  const current = new Map(existing.map((leg) => [leg.key, leg]))

  let added = 0
  let changed = 0
  let removed = 0
  let unchanged = 0
  let untouched = 0

  for (const file of files) {
    const incoming = new Map(file.legs.map((leg) => [leg.key, leg]))

    // Withdraw first: anything inside the period the new file does not carry
    // has stopped operating. Already-withdrawn legs stay as they are so the
    // date they were withdrawn on survives a second import.
    for (const [key, leg] of current) {
      if (!inScope(leg.operatingDate, file.scope)) continue
      if (incoming.has(key)) continue
      if (leg.cancelledAt) continue
      current.set(key, { ...leg, cancelledAt: at })
    }

    // A row outside the period it was imported with is still written: the
    // period bounds what is cleared, not what a file may carry.
    for (const [key, leg] of incoming) current.set(key, leg)
  }

  // Counted against the state this import started from, so the numbers read
  // the same whether one file or four produced them. Every loaded leg falls in
  // exactly one bucket, which is what makes the preview checkable: the five
  // add up to the schedule.
  const before = new Map(existing.map((leg) => [leg.key, leg]))
  const touched = new Set(files.flatMap((file) => file.legs.map((leg) => leg.key)))
  for (const [key, leg] of current) {
    const previous = before.get(key)
    const covered =
      touched.has(key) || files.some((file) => inScope(leg.operatingDate, file.scope))
    // Withdrawn is tested first and without looking at what was there before:
    // two files in one import can add a leg and then withdraw it, and calling
    // those 417 rows "new" would hide the fact that they arrive cancelled.
    if (leg.cancelledAt === at) removed += 1
    else if (!previous) added += 1
    else if (!sameLeg(previous, leg)) changed += 1
    else if (covered) unchanged += 1
    else untouched += 1
  }

  return {
    legs: [...current.values()],
    plan: {
      added,
      changed,
      unchanged,
      removed,
      untouched,
      unknownStations: [],
      unknownTails: [],
      unplaceable: 0,
    },
  }
}

/** Periods that overlap each other, so the preview can name the later winner. */
export function overlappingScopes(files: { fileName: string; scope: ImportScope }[]): string[] {
  const clashing = new Set<string>()
  for (let i = 0; i < files.length; i += 1) {
    for (let j = i + 1; j < files.length; j += 1) {
      const a = files[i].scope
      const b = files[j].scope
      if (a.from <= b.to && b.from <= a.to) {
        clashing.add(files[i].fileName)
        clashing.add(files[j].fileName)
      }
    }
  }
  return [...clashing]
}
